import React from 'react';
import { TrendingUp, TrendingDown, Users, HeartPulse, AlertTriangle, CheckCircle2, BarChart3, Lightbulb, Trophy, Info, UserPlus } from '@/utils/icons';
import { Minus } from 'lucide-react';
import { ReadinessDashboardData } from '@/types';
import TeamReadinessAssessment from './TeamReadinessAssessment';
import { isAnyWearableConnected } from '@/config/wearableProviders';

interface TeamOverviewTabProps {
    selectedTeam: any;
    teamInsights: any;
    rankings: any;
    days: number;
    isDark: boolean;
    handleAthleteDoubleClick: (athleteId: number) => void;
    /** Shown only for coach/admin; triggers Create User modal */
    onAddUser?: () => void;
    userRole?: string;
}

const TeamOverviewTab: React.FC<TeamOverviewTabProps> = ({
    selectedTeam,
    teamInsights,
    rankings,
    days,
    isDark,
    handleAthleteDoubleClick,
    onAddUser,
    userRole
}) => {
    const canAddUser = onAddUser && (userRole === 'coach' || userRole === 'admin');
    // Use readiness data from parent (single source of truth) - no separate API call needed
    const readinessData = selectedTeam?.readiness_data as ReadinessDashboardData | null;
    const readinessLoading = !selectedTeam?.readiness_data && selectedTeam?.id;

    function getRecoveryColor(value: number) {
        if (value >= 67) return '#67AEE6'; // High
        if (value >= 34) return '#F5D76E'; // Medium
        return '#EF4444'; // Low
    }

    // Add color helpers for sleep and strain:
    function getSleepColor() {
      return '#7BA1BB';
    }

    // Calculate athletes in poor readiness (LOW tier)
    const athletesInPoorReadiness = React.useMemo(() => {
        if (!readinessData?.all_athletes) {
            // Fallback to teamInsights if readiness data not available
            return teamInsights.athletesInTheRed || [];
        }
        return readinessData.all_athletes.filter((athlete) => athlete.tier === 'LOW');
    }, [readinessData, teamInsights.athletesInTheRed]);

    const poorReadinessCount = athletesInPoorReadiness.length;

    // Calculate top performers from readiness data (OPTIMAL tier athletes sorted by score)
    const topPerformersFromReadiness = React.useMemo(() => {
        if (!readinessData?.all_athletes) {
            // Fallback to teamInsights if readiness data not available
            return teamInsights.topPerformers || [];
        }
        return readinessData.all_athletes
            .filter((athlete) => athlete.tier === 'OPTIMAL' || athlete.score >= 6.5)
            .sort((a, b) => b.score - a.score)
            .slice(0, 5)
            .map((athlete) => ({
                id: athlete.id,
                name: athlete.name,
                readinessScore: athlete.score,
                sleepTrend: athlete.sleep_trend?.value || 0,
                hrvTrend: athlete.hrv?.trend || 0,
            }));
    }, [readinessData, teamInsights.topPerformers]);

    return (
        <div className="space-y-8">
            {/* Team Health Score & Key Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className={`cgm-metric-card rounded-2xl p-6`}>
                    <div className="flex items-center gap-3 mb-4">
                        <Users className="w-8 h-8 text-blue-600" />
                        <div className="text-sm cgm-text-secondary font-medium flex-1">Total Athletes</div>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                        <div className="text-2xl font-bold theme-text-primary">{selectedTeam.recovery_data?.summary.total_athletes || 0}</div>
                        {canAddUser && (
                            <button
                                type="button"
                                onClick={onAddUser}
                                className="cgm-button p-2 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-105"
                                aria-label="Add user"
                            >
                                <UserPlus className="w-5 h-5 cgm-text-primary" />
                            </button>
                        )}
                    </div>
                </div>

                <div className={`cgm-metric-card rounded-2xl p-6 relative group`}>
                    <div className="flex items-center gap-3 mb-4">
                        <HeartPulse className="w-8 h-8 cgm-icon-warning" />
                        <div className="text-sm cgm-text-secondary font-medium flex-1">Team Health Score</div>
                        <div className="relative">
                            <div className={`w-5 h-5 rounded-full ${isDark ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-300 hover:bg-gray-400'} flex items-center justify-center cursor-help transition-colors duration-200`}>
                                <Info className="w-3 h-3 cgm-text-primary" />
                            </div>

                            {/* Hover Tooltip */}
                            <div className="cgm-tooltip absolute bottom-full right-0 mb-2 w-80 border rounded-lg shadow-xl p-4 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity duration-200 z-50">
                                <div className="space-y-3">
                                    <h4 className="text-sm font-semibold cgm-text-primary border-b border-gray-200 dark:border-gray-600 pb-2">How it's calculated:</h4>
                                    <div className="space-y-2 text-xs cgm-text-secondary">
                                        <div className="flex items-center justify-between">
                                            <span>• Average Recovery Score</span>
                                            <span className="font-medium">{rankings.recovery.length > 0 ?
                                                `${(rankings.recovery.reduce((sum: number, r: any) => sum + r.latestRecovery, 0) / rankings.recovery.length).toFixed(1)}%` : 'N/A'}</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span>• Average Sleep Score</span>
                                            <span className="font-medium">{rankings.sleep.length > 0 ?
                                                `${(rankings.sleep.reduce((sum: number, s: any) => sum + s.averageQuality, 0) / rankings.sleep.length).toFixed(1)}%` : 'N/A'}</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span>• Data Completeness</span>
                                            <span className="font-medium">{
                                                selectedTeam.recovery_data?.summary.total_athletes
                                                    ? `${((selectedTeam.recovery_data.athletes_data.filter((a: any) => isAnyWearableConnected(a) &&
                                                        (rankings.recovery.find((r: any) => r.name === a.athlete_name)?.dataPoints || 0) >= Math.min(days * 0.3, 7)).length /
                                                        selectedTeam.recovery_data.athletes_data.filter((a: any) => isAnyWearableConnected(a)).length) * 100).toFixed(1)}%`
                                                    : 'N/A'
                                            }</span>
                                        </div>
                                        <div className="text-xs cgm-text-secondary mt-1 italic">
                                            Athletes with ≥{Math.min(days * 0.3, 7).toFixed(0)} days of data
                                        </div>
                                    </div>
                                    <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                                        <div className="text-xs cgm-text-secondary">
                                            <strong>Score Ranges:</strong><br />
                                            <span className="text-green-600">70-100 Excellent</span> •
                                            <span className="text-yellow-600">50-69 Good</span><br />
                                            <span className="text-orange-600">30-49 Needs Attention</span> •
                                            <span className="text-red-600">0-29 Critical</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Tooltip Arrow */}
                                <div className="cgm-tooltip-arrow absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-6 border-l-transparent border-r-transparent"></div>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center space-x-2">
                        <div className="text-2xl font-bold theme-text-primary">{teamInsights.teamHealthScore}</div>
                    </div>
                </div>

                <div className={`cgm-metric-card rounded-2xl p-6`}>
                    <div className="flex items-center gap-3 mb-4">
                        {poorReadinessCount > 0 ? (
                            <AlertTriangle className="w-8 h-8 cgm-icon-warning" />
                        ) : (
                            <CheckCircle2 className="w-8 h-8 cgm-icon-success" />
                        )}
                        <div className="text-sm cgm-text-secondary font-medium">In the Red</div>
                    </div>
                    <div className="text-2xl font-bold theme-text-primary">{poorReadinessCount}</div>
                    {poorReadinessCount > 0 && (
                        <div className="mt-3 space-y-1 max-h-24 overflow-y-auto">
                            {athletesInPoorReadiness.map((athlete: any) => (
                                <div
                                    key={athlete.id || athlete.name}
                                    className="text-sm cgm-text-secondary hover:!text-black dark:hover:!text-white cursor-pointer truncate transition-colors"
                                    onClick={() => handleAthleteDoubleClick(athlete.id)}
                                    title={athlete.name}
                                >
                                    {athlete.name}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className={`cgm-metric-card rounded-2xl p-6`}>
                    <div className="flex items-center gap-3 mb-4">
                        <BarChart3 className="w-8 h-8 cgm-icon-data" />
                        <div className="text-sm cgm-text-secondary font-medium">Data Coverage</div>
                    </div>
                    <div className="text-2xl font-bold theme-text-primary">
                        {selectedTeam.recovery_data?.summary.total_athletes
                            ? `${Math.round((selectedTeam.recovery_data.summary.athletes_with_data / selectedTeam.recovery_data.summary.total_athletes) * 100)}%`
                            : '0%'}
                    </div>
                </div>
            </div>

            {/* Team Trends Analysis */}
            {teamInsights.teamTrends && (
                <div className={`cgm-metric-card rounded-2xl p-6 relative group`}>
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                            <TrendingUp className="w-8 h-8 cgm-icon-data" />
                            <div>
                                <h3 className="text-xl font-bold cgm-text-primary">Team Performance Trends</h3>
                                <p className="text-sm cgm-text-secondary">Overall team performance direction over the {days}-day period</p>
                            </div>
                        </div>
                        <div className="relative">
                            <div className={`w-6 h-6 rounded-full ${isDark ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-300 hover:bg-gray-400'} flex items-center justify-center cursor-help transition-colors duration-200`}>
                                <svg className="w-4 h-4 cgm-text-primary" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                                </svg>
                            </div>
                            {/* Hover Tooltip */}
                            <div className="cgm-tooltip absolute bottom-full right-0 mb-2 w-80 border rounded-lg shadow-xl p-4 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity duration-200 z-50">
                                <div className="space-y-3">
                                    <h4 className="text-sm font-semibold cgm-text-primary border-b border-gray-200 dark:border-gray-600 pb-2">Trend Interpretation</h4>
                                    <div className="text-xs cgm-text-secondary space-y-2">
                                        <p><strong>Recovery:</strong> Positive trends indicate improving fitness and adaptation</p>
                                        <p><strong>Sleep:</strong> Upward trends show better sleep hygiene and recovery practices</p>
                                        <p><strong>Strain:</strong> Monitor increases to prevent overtraining, decreases may indicate detraining</p>
                                    </div>
                                </div>
                                {/* Tooltip Arrow */}
                                <div className="cgm-tooltip-arrow absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-6 border-l-transparent border-r-transparent"></div>
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Recovery Trend */}
                        <div className="rounded-xl p-4 border border-gray-200 dark:border-neutral-700/50">
                            <div className="flex items-center justify-between mb-2">
                                <h4 className="text-sm font-semibold cgm-text-primary">Recovery Trend</h4>
                                <div className={`${teamInsights.teamTrends.recoveryTrend > 0 ? 'text-green-600' : teamInsights.teamTrends.recoveryTrend < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                                    {teamInsights.teamTrends.recoveryTrend > 0 ? (
                                        <TrendingUp className="w-5 h-5" />
                                    ) : teamInsights.teamTrends.recoveryTrend < 0 ? (
                                        <TrendingDown className="w-5 h-5" />
                                    ) : (
                                        <Minus className="w-5 h-5" />
                                    )}
                                </div>
                            </div>
                            <div className={`text-2xl font-bold ${teamInsights.teamTrends.recoveryTrend > 0 ? 'text-green-600' : teamInsights.teamTrends.recoveryTrend < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                                {teamInsights.teamTrends.recoveryTrend > 0 ? '+' : ''}{teamInsights.teamTrends.recoveryTrend.toFixed(1)}
                            </div>
                            <div className="text-xs cgm-text-secondary mt-1">
                                {teamInsights.teamTrends.recoveryTrend > 5 ? 'Improving significantly' :
                                    teamInsights.teamTrends.recoveryTrend > 2 ? 'Gradually improving' :
                                        teamInsights.teamTrends.recoveryTrend > -2 ? 'Stable' :
                                            teamInsights.teamTrends.recoveryTrend > -5 ? 'Slightly declining' : 'Declining significantly'}
                            </div>
                        </div>
                        {/* Sleep Trend */}
                        <div className="rounded-xl p-4 border border-gray-200 dark:border-neutral-700/50">
                            <div className="flex items-center justify-between mb-2">
                                <h4 className="text-sm font-semibold cgm-text-primary">Sleep Quality Trend</h4>
                                <div className={`${teamInsights.teamTrends.sleepTrend > 0 ? 'text-green-600' : teamInsights.teamTrends.sleepTrend < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                                    {teamInsights.teamTrends.sleepTrend > 0 ? (
                                        <TrendingUp className="w-5 h-5" />
                                    ) : teamInsights.teamTrends.sleepTrend < 0 ? (
                                        <TrendingDown className="w-5 h-5" />
                                    ) : (
                                        <Minus className="w-5 h-5" />
                                    )}
                                </div>
                            </div>
                            <div className={`text-2xl font-bold ${teamInsights.teamTrends.sleepTrend > 0 ? 'text-green-600' : teamInsights.teamTrends.sleepTrend < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                                {teamInsights.teamTrends.sleepTrend > 0 ? '+' : ''}{teamInsights.teamTrends.sleepTrend.toFixed(1)}
                            </div>
                            <div className="text-xs cgm-text-secondary mt-1">
                                {teamInsights.teamTrends.sleepTrend > 3 ? 'Improving significantly' :
                                    teamInsights.teamTrends.sleepTrend > 1 ? 'Gradually improving' :
                                        teamInsights.teamTrends.sleepTrend > -1 ? 'Stable' :
                                            teamInsights.teamTrends.sleepTrend > -3 ? 'Slightly declining' : 'Declining significantly'}
                            </div>
                        </div>
                        {/* Strain Trend */}
                        <div className="rounded-xl p-4 border border-gray-200 dark:border-neutral-700/50">
                            <div className="flex items-center justify-between mb-2">
                                <h4 className="text-sm font-semibold cgm-text-primary">Training Strain Trend</h4>
                                <div className={`${teamInsights.teamTrends.strainTrend > 0 ? 'text-orange-600' : teamInsights.teamTrends.strainTrend < 0 ? 'text-blue-600' : 'text-gray-500'}`}>
                                    {teamInsights.teamTrends.strainTrend > 0 ? (
                                        <TrendingUp className="w-5 h-5" />
                                    ) : teamInsights.teamTrends.strainTrend < 0 ? (
                                        <TrendingDown className="w-5 h-5" />
                                    ) : (
                                        <Minus className="w-5 h-5" />
                                    )}
                                </div>
                            </div>
                            <div className={`text-2xl font-bold ${teamInsights.teamTrends.strainTrend > 0 ? 'text-orange-600' : teamInsights.teamTrends.strainTrend < 0 ? 'text-blue-600' : 'text-gray-500'}`}>
                                {teamInsights.teamTrends.strainTrend > 0 ? '+' : ''}{teamInsights.teamTrends.strainTrend.toFixed(1)}
                            </div>
                            <div className="text-xs cgm-text-secondary mt-1">
                                {teamInsights.teamTrends.strainTrend > 2 ? 'Increasing intensity' :
                                    teamInsights.teamTrends.strainTrend > 0.5 ? 'Gradual increase' :
                                        teamInsights.teamTrends.strainTrend > -0.5 ? 'Stable workload' :
                                            teamInsights.teamTrends.strainTrend > -2 ? 'Decreasing load' : 'Significantly reduced'}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Team Readiness Assessment - Team Roster + Spider Chart & Metrics */}
            {!readinessLoading && readinessData && (
                <TeamReadinessAssessment 
                    athletes={readinessData.all_athletes}
                    isDark={isDark}
                    onAthleteClick={handleAthleteDoubleClick}
                />
            )}

            {/* Key Insights & Action Items */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Key Insights */}
                <div className={`cgm-metric-card rounded-2xl p-6`}>
                    <div className="flex items-center space-x-3 mb-4">
                        <Lightbulb className="w-8 h-8 text-blue-600" />
                        <h3 className="text-lg font-bold cgm-text-primary">Key Insights</h3>
                    </div>

                    {teamInsights.keyInsights.length > 0 ? (
                        <div className="space-y-3">
                            {teamInsights.keyInsights.map((insight: string, index: number) => (
                                <div key={index} className={`flex items-start space-x-3 p-3 rounded-lg ${isDark ? "bg-gray-700/20" : "bg-blue-100/40"}`}>
                                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                                    <p className="text-sm cgm-text-primary leading-relaxed">{insight}</p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm cgm-text-secondary">No specific insights available for the current data set.</p>
                    )}
                </div>

                {/* Action Items */}
                <div className={`cgm-metric-card rounded-2xl p-6`}>
                    <div className="flex items-center space-x-3 mb-4">
                        <CheckCircle2 className="w-8 h-8 cgm-icon-success" />
                        <h3 className="text-lg font-bold cgm-text-primary">Action Items</h3>
                    </div>

                    {teamInsights.actionItems.length > 0 ? (
                        <div className="space-y-3">
                            {teamInsights.actionItems.map((action: string, index: number) => (
                                <div key={index} className={`flex items-start space-x-3 p-3 rounded-lg ${isDark ? 'bg-green-700/20' : 'bg-green-100/40'}`}>
                                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                                    <p className="text-sm cgm-text-primary leading-relaxed">{action}</p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm cgm-text-secondary">No immediate actions required. Team is performing well.</p>
                    )}
                </div>
            </div>

            {/* Top Performers & Data Quality */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Performers */}
                {topPerformersFromReadiness.length > 0 && (
                    <div className={`cgm-metric-card rounded-2xl p-6`}>
                        <div className="flex items-center space-x-3 mb-4">
                            <Trophy className="w-8 h-8 cgm-icon-activity" />
                            <h3 className="text-lg font-bold cgm-text-primary">Top Performers</h3>
                        </div>

                        <div className="space-y-3">
                            {topPerformersFromReadiness.map((performer: any, index: number) => (
                                <div
                                    key={performer.name}
                                    className={`flex items-center space-x-3 p-3 rounded-lg ${isDark ? 'bg-yellow-900/10' : 'bg-yellow-50'} hover:scale-105 transition-all duration-200 cursor-pointer`}
                                    onClick={() => handleAthleteDoubleClick(performer.id)}
                                >
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${index === 0 ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-black' :
                                        index === 1 ? 'bg-gradient-to-r from-gray-300 to-gray-400 text-gray-800' :
                                            index === 2 ? 'bg-gradient-to-r from-orange-400 to-orange-500 text-white' :
                                                isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700 text-gray-200' : 'bg-gradient-to-r from-gray-200 to-gray-300 text-gray-700'
                                        }`}>
                                        {index + 1}
                                    </div>
                                    <div className="flex-1">
                                        <div className="font-medium cgm-text-secondary text-sm">{performer.name}</div>
                                        <div className="text-xs cgm-text-secondary">
                                            Readiness: {(performer.readinessScore * 10).toFixed(0)}% |
                                            Sleep: {performer.sleepTrend > 0 ? `${performer.sleepTrend.toFixed(1)}h` : 'N/A'} |
                                            HRV Trend: {performer.hrvTrend > 0 ? '+' : ''}{performer.hrvTrend.toFixed(1)}%
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Data Quality Issues */}
                {teamInsights.dataQualityIssues.length > 0 && (
                    <div className={`cgm-metric-card rounded-2xl p-6`}>
                        <div className="flex items-center space-x-3 mb-4">
                            <AlertTriangle className="w-8 h-8 cgm-icon-warning" />
                            <h3 className="text-lg font-bold cgm-text-primary">Data Quality Issues</h3>
                        </div>

                        <div className="space-y-3">
                            {teamInsights.dataQualityIssues.map((issue: any, index: number) => (
                                <div key={index} className="flex items-center space-x-3 p-3 rounded-lg bg-opacity-50 bg-orange-100 dark:bg-orange-900/20">
                                    <div className={`w-3 h-3 rounded-full ${issue.connected ? 'bg-orange-500' : 'bg-red-500'}`}></div>
                                    <div className="flex-1">
                                        <div className="font-medium cgm-text-secondary text-sm">{issue.name}</div>
                                        <div className="text-xs cgm-text-secondary">{issue.issue}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TeamOverviewTab; 